import { ActionMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Delete, EditFolder, Favorite16, FolderClosed, MoveToFolder, Overflow } from 'ui/icons';
import { colors } from 'ui/theme';

import styled from 'styled-components';
import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { GraphQLCampaignSubfolder } from '../types';
import { useSelectionContext } from './contexts/SelectionContext';
import { useSelector } from 'react-redux';
import { selectIsCampaignsFavoritesFeatureFlagOn } from 'core/userContext/selectors';

export const displayContent = {
  delete: 'Delete folder',
  move: 'Move to folder',
  new: 'New subfolder',
  rename: 'Rename folder',
};

const StyledFolderClosed = styled(FolderClosed)`
  && path {
    stroke: none;
  }
`;

type Props = {
  folder: GraphQLCampaignSubfolder;
  onFavoriteClick: () => void;
  isFavoriteSelected: boolean;
};

export const CampaignFolderOverflowMenu = ({
  folder,
  isFavoriteSelected,
  onFavoriteClick,
}: Props) => {
  const isCampaignsFavoritesFeatureFlagOn = useSelector(selectIsCampaignsFavoritesFeatureFlagOn);
  const { selectItem } = useSelectionContext();
  const modals = useCampaignFolderModals();
  const setFolderAsCurrentSelection = () => {
    selectItem(folder);
  };

  const overflowOptions = [
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <StyledFolderClosed height={18} width={18} />
          <span>{displayContent.new}</span>
        </>
      ),
      id: 'new-subfolder',
      onClick: () => {
        setFolderAsCurrentSelection();
        modals.create.toggle(true);
      },
    },
    ...(isCampaignsFavoritesFeatureFlagOn
      ? [
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <Favorite16 color={colors.grey50} height={18} width={18} />
                <span>{isFavoriteSelected ? 'Remove from' : 'Add to'} favorites</span>
              </>
            ),
            id: 'favorite-folder',
            onClick: onFavoriteClick,
          },
        ]
      : []),
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <MoveToFolder color={colors.grey50} height={18} width={18} />
          <span>{displayContent.move}</span>
        </>
      ),
      id: 'move-to-folder',
      onClick: () => {
        setFolderAsCurrentSelection();
        modals.move.toggle(true);
      },
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <EditFolder color={colors.grey50} height={18} width={18} />
          <span>{displayContent.rename}</span>
        </>
      ),
      id: 'rename-folder',
      onClick: () => {
        setFolderAsCurrentSelection();
        modals.rename.toggle(true);
      },
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Delete color={colors.grey50} height={18} width={18} />
          <span>{displayContent.delete}</span>
        </>
      ),
      id: 'delete-folder',
      onClick: () => {
        setFolderAsCurrentSelection();
        modals.delete.toggle(true);
      },
    },
  ];

  return (
    <ActionMenu align={MENU_ALIGNMENTS.RIGHT} options={overflowOptions}>
      <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
        <Overflow color={colors.grey90} />
      </IconButton>
    </ActionMenu>
  );
};
